import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ExternalLink, LogOut, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { consumeReturnPath } from "../lib/returnPath";
import { resolvePostAuthPath } from "../lib/dashboardPaths";

const PARENT_SLUGS = ["terms", "parent-agreement"];
const PARENT_LABELS = {
  terms: "Terms of Service",
  "parent-agreement": "Parent Agreement",
};

const STUDENT_SLUGS = ["privacy", "terms"];

export default function CustomerOnboarding({ mode = "parent" }) {
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh, logout } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acceptances, setAcceptances] = useState({});
  const [nameConfirmed, setNameConfirmed] = useState(false);

  const slugOrder = mode === "parent" ? PARENT_SLUGS : STUDENT_SLUGS;
  const title = mode === "parent" ? "Parent onboarding" : "Student onboarding";
  const completePath = mode === "parent" ? "/onboarding/parent/complete" : "/onboarding/student/complete";

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    setLoading(true);
    api
      .get("/legal-documents")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const ordered = slugOrder.map((slug) => list.find((d) => d.slug === slug)).filter(Boolean);
        setDocs(ordered);
      })
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [authLoading, user, slugOrder]);

  const allAccepted = useMemo(() => {
    if (!docs.length) return false;
    if (!docs.every((d) => acceptances[d.slug])) return false;
    if (mode === "parent" && !nameConfirmed) return false;
    return true;
  }, [docs, acceptances, mode, nameConfirmed]);

  const submit = async () => {
    if (!allAccepted) {
      toast.error("Please accept all required documents before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(completePath, {
        acceptances: docs.map((d) => ({
          document_id: d.id,
          slug: d.slug,
          title: d.title,
          version: d.version ?? 1,
          accepted: true,
          accepted_at: new Date().toISOString(),
        })),
        name_confirmation: mode === "parent" ? nameConfirmed : undefined,
      });
      const session = await refresh();
      toast.success("Onboarding complete.");
      const nextPath = resolvePostAuthPath(session, consumeReturnPath());
      navigate(nextPath, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-onboarding-title"
      data-testid={`${mode}-onboarding-modal`}
    >
      <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-[3px]" aria-hidden />

      <div className="relative w-full max-w-lg max-h-[min(90vh,680px)] flex flex-col rounded-2xl border border-aus-onyx/10 bg-white shadow-2xl overflow-hidden">
        <div className="shrink-0 flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-aus-onyx/8">
          <div className="flex items-start gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-aus-cobalt/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-aus-cobalt" />
            </div>
            <div className="min-w-0">
              <span className="tiny-caps">{title}</span>
              <h2 id="customer-onboarding-title" className="font-heading text-xl font-bold mt-0.5 tracking-tight">
                Accept terms to continue
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-aus-onyx/55 hover:text-aus-onyx transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {authLoading || loading ? (
            <p className="text-sm text-center text-aus-onyx/55 py-8">Loading agreements...</p>
          ) : (
            <>
              <p className="text-sm text-aus-onyx/65 mb-4">
                {mode === "parent"
                  ? "Before managing your linked student account, please read and accept the required parent agreements."
                  : "Before you can book lessons or use your student account, please confirm you understand our Terms and Privacy Policy."}
              </p>

              {user && user.email_verified === false ? (
                <div className="mb-4 rounded-xl border border-aus-cobalt/20 bg-aus-cobalt/5 px-3 py-2.5 flex items-start gap-2.5">
                  <Mail className="w-4 h-4 text-aus-cobalt shrink-0 mt-0.5" />
                  <p className="text-xs text-aus-onyx/75">
                    We sent a verification link to <strong>{user.email}</strong>. Please verify it to confirm account ownership and keep your account emails working properly.
                  </p>
                </div>
              ) : null}

              {!docs.length ? (
                <div className="rounded-xl p-4 text-sm text-amber-800 bg-amber-50/80 border border-amber-200/60">
                  Required documents are not yet available. Please contact support@austuition.com.au.
                </div>
              ) : (
                <div className="rounded-xl border border-aus-onyx/8 bg-aus-cream/40 divide-y divide-aus-onyx/8">
                  {docs.map((doc) => {
                    const label = mode === "parent" ? PARENT_LABELS[doc.slug] || doc.title : doc.title;
                    return (
                      <label key={doc.slug} className="flex items-start gap-2.5 text-sm p-3.5 cursor-pointer hover:bg-white/60 transition-colors">
                        <input
                          type="checkbox"
                          className="mt-0.5 shrink-0"
                          checked={Boolean(acceptances[doc.slug])}
                          onChange={(e) => setAcceptances((prev) => ({ ...prev, [doc.slug]: e.target.checked }))}
                        />
                        <span className="flex-1 min-w-0">
                          I have read and agree to the <strong>{label}</strong>.{" "}
                          <Link
                            to={`/legal/${doc.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-aus-cobalt font-semibold inline-flex items-center gap-0.5 hover:underline whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Read more <ExternalLink className="w-3 h-3" />
                          </Link>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {mode === "parent" && docs.length ? (
                <label className="mt-3 flex items-start gap-2.5 rounded-xl border border-aus-onyx/8 bg-white p-3.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 shrink-0"
                    checked={nameConfirmed}
                    onChange={(e) => setNameConfirmed(e.target.checked)}
                  />
                  <span>
                    I confirm this account is registered in my own name and I am responsible for the linked student account.
                  </span>
                </label>
              ) : null}

              <p className="text-xs text-aus-onyx/45 mt-4 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-aus-emerald shrink-0" />
                You only need to complete this once.
              </p>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-aus-onyx/8 px-5 py-4 bg-white">
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !allAccepted || loading || authLoading}
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {submitting ? "Saving..." : "I accept — continue to Austuition"} <ArrowRight className="w-4 h-4" />
          </button>
          {!allAccepted && docs.length > 0 && !loading ? (
            <p className="text-xs text-center text-aus-onyx/45 mt-2">
              Accept every document above to continue.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
